import React, { useEffect } from 'react';
import styled from 'styled-components';
import { colors, breakpoints } from '../../styles/variables';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  z-index: 1000;
  overflow-y: auto;
`;

const Dialog = styled.div`
  background-color: ${colors.white};
  border-radius: 16px;
  width: 100%;
  max-width: 960px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  position: relative;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, ${(p) => p.$color} 0%, rgba(0, 0, 0, 0.55) 100%);
  color: ${colors.white};
  padding: 40px 40px 32px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 32px 24px 24px;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: ${colors.white};
  font-size: 22px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }
`;

const CourseLabel = styled.div`
  font-size: 13px;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 8px;
`;

const Title = styled.h2`
  font-size: 30px;
  font-weight: 700;
  margin: 0 0 14px;
  line-height: 1.3;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 24px;
  }
`;

const Lead = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 18px;
`;

const SkillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SkillChip = styled.span`
  background: rgba(255, 255, 255, 0.18);
  color: ${colors.white};
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
`;

const Body = styled.div`
  padding: 32px 40px 40px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 24px 24px 32px;
  }
`;

const ProjectImg = styled.img`
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  margin-bottom: 28px;
  background-color: ${colors.lightGray};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 28px;

  & > * {
    min-width: 0;
  }

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.section`
  & + & {
    margin-top: 24px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.primary};
  margin: 0 0 12px;
`;

const Paragraph = styled.p`
  font-size: 15px;
  line-height: 1.8;
  color: ${colors.textGray};
  margin: 0;
  white-space: pre-line;
`;

const BulletList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: ${colors.textGray};

  li {
    font-size: 14px;
    line-height: 1.7;
    margin-bottom: 6px;
  }
`;

const TechList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const TechChip = styled.span`
  background-color: ${colors.lightGray};
  color: ${colors.tertiary};
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
`;

const ExternalLink = styled.a`
  display: block;
  margin-top: 8px;
  color: ${colors.secondary};
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ProjectDetailModal = ({ project, course, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Dialog>
        <Hero $color={course.color}>
          <CloseBtn aria-label="닫기" onClick={onClose}>
            ×
          </CloseBtn>
          <CourseLabel>{course.name} · 실습 프로젝트</CourseLabel>
          <Title>{project.title}</Title>
          <Lead>{project.description}</Lead>
          <SkillRow>
            {project.skills.map((skill, idx) => (
              <SkillChip key={idx}>{skill}</SkillChip>
            ))}
          </SkillRow>
        </Hero>

        <Body>
          <ProjectImg src={project.image} alt={project.title} />

          <Grid>
            <div>
              {project.overview && (
                <Section>
                  <SectionTitle>프로젝트 개요</SectionTitle>
                  <Paragraph>{project.overview}</Paragraph>
                </Section>
              )}

              {project.features?.length > 0 && (
                <Section>
                  <SectionTitle>핵심 기능</SectionTitle>
                  <BulletList>
                    {project.features.map((f, idx) => (
                      <li key={idx}>{f}</li>
                    ))}
                  </BulletList>
                </Section>
              )}

              {project.learningGoals?.length > 0 && (
                <Section>
                  <SectionTitle>학습 목표</SectionTitle>
                  <BulletList>
                    {project.learningGoals.map((g, idx) => (
                      <li key={idx}>{g}</li>
                    ))}
                  </BulletList>
                </Section>
              )}
            </div>

            <div>
              {project.techStack?.length > 0 && (
                <Section>
                  <SectionTitle>기술 스택</SectionTitle>
                  <TechList>
                    {project.techStack.map((t, idx) => (
                      <TechChip key={idx}>{t}</TechChip>
                    ))}
                  </TechList>
                </Section>
              )}

              {(project.demoLink || project.repoLink) && (
                <Section>
                  <SectionTitle>외부 링크</SectionTitle>
                  {project.demoLink && (
                    <ExternalLink
                      href={project.demoLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      라이브 데모 보기 →
                    </ExternalLink>
                  )}
                  {project.repoLink && (
                    <ExternalLink
                      href={project.repoLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      소스 코드 보기 →
                    </ExternalLink>
                  )}
                </Section>
              )}
            </div>
          </Grid>
        </Body>
      </Dialog>
    </Overlay>
  );
};

export default ProjectDetailModal;
